import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { formatDecimal, parseDecimal } from "@/lib/decimal";

// Einheitliche Zahleneingabe der App (Safari-sicher, kein <input type="number">):
// type="text" + inputMode="decimal" (Ganzzahlen: "numeric"); Parser/Optionen in lib/decimal.js.
export function DecimalField({ value, onChange, error, hint, testId, className, integer = false, align = "right", ...rest }) {
  return (
    <div className={cn("flex flex-col", align === "right" ? "items-end" : "items-stretch")}>
      <Input type="text" inputMode={integer ? "numeric" : "decimal"} autoComplete="off" spellCheck={false} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)} aria-invalid={!!error}
        className={cn("font-mono", align === "right" && "text-right", error && "border-rose-500 focus-visible:ring-rose-500 bg-rose-50", className)}
        data-testid={testId} {...rest} />
      {error && <p className={cn("text-[10px] leading-tight text-rose-600 mt-0.5", align === "right" && "text-right max-w-[9rem]")} role="alert" data-testid={`${testId}-error`}>{error}</p>}
      {!error && hint}
    </div>
  );
}

// Formularfeld: Text bleibt im Eltern-State; bei Blur/Enter wird geprüft und deutsch formatiert ("0.8" → "0,8").
// Ungültig → roter Rahmen + Meldung, der Text bleibt stehen (Eltern prüfen beim Speichern mit parseFields).
export function NumberField({ value, onChange, opts = {}, error, onEnter, ...rest }) {
  const [own, setOwn] = useState("");
  const check = () => {
    const r = parseDecimal(value, opts);
    if (r.error) { setOwn(r.error); return; }
    setOwn("");
    const t = formatDecimal(r.value, opts);
    if (t !== (value ?? "")) onChange(t);
  };
  return (
    <DecimalField value={value} integer={opts.integer} error={own || error}
      onChange={(t) => { setOwn(""); onChange(t); }} onBlur={check}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); check(); onEnter?.(); } }} {...rest} />
  );
}

// Fehler je Feld (vom Speichern); Eingabe in einem Feld löscht dessen Fehler.
export function useFieldErrors() {
  const [errors, setErrors] = useState({});
  const clear = (k) => setErrors((e) => (e[k] ? { ...e, [k]: "" } : e));
  return [errors, setErrors, clear];
}

// Tabellen-Variante: übernimmt den Wert erst bei Blur/Enter (onCommit), ungültige Eingaben werden nie als 0 gesendet.
export function DecimalInput({ value, onCommit, opts = {}, renderHint, testId, disabled, className, ...rest }) {
  const fmt = (v) => formatDecimal(v, opts);
  const [text, setText] = useState(fmt(value));
  const [error, setError] = useState("");
  const dirty = useRef(false);

  useEffect(() => {
    if (!dirty.current) { setText(fmt(value)); setError(""); }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    if (!dirty.current) return;
    const r = parseDecimal(text, opts);
    if (r.error) { setError(r.error); return; }
    dirty.current = false;
    setError("");
    setText(fmt(r.value));
    if (Math.abs(r.value - Number(value)) > 1e-9) onCommit(r.value);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    if (e.key === "Escape") { dirty.current = false; setText(fmt(value)); setError(""); }
  };

  const live = parseDecimal(text, opts);
  return (
    <DecimalField value={text} disabled={disabled} error={error} testId={testId} integer={opts.integer} className={cn("h-8", className)}
      onChange={(t) => { dirty.current = true; setText(t); if (error && !parseDecimal(t, opts).error) setError(""); }}
      onBlur={commit} onKeyDown={onKeyDown}
      hint={renderHint ? renderHint(live.error ? null : live.value, dirty.current) : null} {...rest} />
  );
}
